import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressHeader from '../../components/ui/ProgressHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProfileCard from './components/ProfileCard';
import TrustSignals from './components/TrustSignals';
import ProfileSelector from './components/ProfileSelector';

const ProfileSelectionLanding = () => {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState('');
  const [showMobileSelector, setShowMobileSelector] = useState(false);

  // Mock profile data
  const profiles = [
    {
      id: 'it-support',
      title: 'IT Support Engineer',
      icon: 'Monitor',
      description: 'Master technical troubleshooting, system administration, and customer support skills to become a certified IT Support professional.',
      duration: '4-6 months',
      fee: 4999,
      benefits: [
        'Industry-recognized certifications',
        'Hands-on lab experience',
        'Real-world project portfolio',
        '100% placement assistance',
        'Dedicated career counseling'
      ],
      curriculum: [
        'Hardware & Software Troubleshooting',
        'Network Administration',
        'Windows & Linux Systems',
        'Help Desk Management',
        'Security Fundamentals',
        'Cloud Computing Basics'
      ]
    },
    {
      id: 'fullstack-developer',
      title: 'Full Stack Web Developer',
      icon: 'Code',
      description: 'Learn full-stack development with modern technologies, build real applications, and launch your software engineering career.',
      duration: '6-8 months',
      fee: 6999,
      benefits: [
        'Full-stack development training',
        'Modern framework expertise',
        'Live project experience',
        'GitHub portfolio building',
        '100% placement assistance'
      ],
      curriculum: [
        'Frontend Development (React)',
        'Backend Development (Node.js)',
        'Database Management',
        'API Development',
        'Version Control (Git)',
        'Deployment & DevOps'
      ]
    },
    {
      id: 'mobile-app-developer',
      title: 'Mobile App Developer',
      icon: 'Smartphone',
      description: 'Master mobile app development for iOS and Android platforms using modern frameworks and native technologies.',
      duration: '6-8 months',
      fee: 7499,
      benefits: [
        'Cross-platform development skills',
        'Native iOS & Android expertise',
        'App Store deployment guide',
        'Real device testing projects',
        '100% placement assistance'
      ],
      curriculum: [
        'React Native Development',
        'Flutter Framework',
        'Native iOS (Swift)',
        'Native Android (Kotlin)',
        'Mobile UI/UX Design',
        'App Store Optimization'
      ]
    },
    {
      id: 'devops',
      title: 'DevOps Engineer',
      icon: 'Settings',
      description: 'Master automation, CI/CD pipelines, and infrastructure management to become a certified DevOps professional driving modern software delivery.',
      duration: '5-7 months',
      fee: 7999,
      benefits: [
        'Cloud platform certifications',
        'Container orchestration skills',
        'Infrastructure as Code training',
        'Real-world pipeline projects',
        '100% placement assistance'
      ],
      curriculum: [
        'Docker & Kubernetes',
        'CI/CD Pipeline Development',
        'AWS/Azure Cloud Services',
        'Infrastructure as Code (Terraform)',
        'Monitoring & Logging',
        'Security & Compliance'
      ]
    },
    {
      id: 'data-analytics',
      title: 'Data Analytics',
      icon: 'BarChart3',
      description: 'Transform data into actionable insights with advanced analytics, visualization tools, and statistical modeling techniques.',
      duration: '4-6 months',
      fee: 5999,
      benefits: [
        'Industry-standard analytics tools',
        'Statistical modeling expertise',
        'Business intelligence training',
        'Real-world case studies',
        '100% placement assistance'
      ],
      curriculum: [
        'Python/R for Data Analysis',
        'SQL & Database Management',
        'Data Visualization (Tableau/Power BI)',
        'Statistical Analysis',
        'Machine Learning Basics',
        'Business Intelligence'
      ]
    },
    {
      id: 'artificial-intelligence',
      title: 'Artificial Intelligence',
      icon: 'Brain',
      description: 'Dive into AI and machine learning to build intelligent systems, neural networks, and cutting-edge AI applications.',
      duration: '8-10 months',
      fee: 9999,
      benefits: [
        'Deep learning frameworks mastery',
        'Neural network development',
        'AI project portfolio creation',
        'Research methodology training',
        '100% placement assistance'
      ],
      curriculum: [
        'Machine Learning Algorithms',
        'Deep Learning & Neural Networks',
        'Natural Language Processing',
        'Computer Vision',
        'TensorFlow & PyTorch',
        'AI Ethics & Deployment'
      ]
    }
  ];

  const handleProfileSelect = (profileId) => {
    setSelectedProfile(profileId);
    // Store selection in localStorage for persistence
    localStorage.setItem('selectedProfile', profileId);
  };

  const handleStartRegistration = () => {
    if (selectedProfile) {
      // Store selected profile data for use in registration
      const profileData = profiles?.find(p => p?.id === selectedProfile);
      localStorage.setItem('profileData', JSON.stringify(profileData));
      navigate('/multi-step-registration-form');
    }
  };

  // Load saved profile selection on mount
  useEffect(() => {
    const savedProfile = localStorage.getItem('selectedProfile');
    if (savedProfile) {
      setSelectedProfile(savedProfile);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressHeader />
      
      {/* Main Content */}
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Choose Your Career Path
            </h1>
            <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
              Start your journey towards a successful tech career with our comprehensive training and placement programs designed for aspiring professionals.
            </p>
          </div>

          {/* Profile Cards - Desktop */}
          <div className="hidden lg:grid lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-12">
            {profiles?.map((profile) => (
              <ProfileCard
                key={profile?.id}
                profile={profile}
                isSelected={selectedProfile === profile.id}
                onSelect={handleProfileSelect}
              />
            ))}
          </div>

          {/* Profile Cards - Mobile/Tablet */}
          <div className="lg:hidden space-y-6 mb-8">
            {profiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                isSelected={selectedProfile === profile.id}
                onSelect={handleProfileSelect}
              />
            ))}
          </div>

          {/* Profile Selector Dropdown */}
          <div className="max-w-2xl mx-auto mb-12">
            <ProfileSelector
              profiles={profiles}
              selectedProfile={selectedProfile}
              onProfileChange={handleProfileSelect}
            />
          </div>

          {/* CTA Section */}
          <div className="text-center mb-16">
            <Button
              variant={selectedProfile ? 'default' : 'outline'}
              size="lg"
              disabled={!selectedProfile}
              onClick={handleStartRegistration}
              iconName="ArrowRight"
              iconPosition="right"
              className="px-8 py-4"
            >
              {selectedProfile ? 'Start Registration' : 'Select a Program to Continue'}
            </Button>
            
            {selectedProfile && (
              <p className="text-sm text-text-secondary mt-3">
                You selected: <span className="font-medium text-foreground">
                  {profiles.find(p => p.id === selectedProfile)?.title}
                </span>
              </p>
            )}
          </div>

          {/* Trust Signals */}
          <TrustSignals />

          {/* Additional Information */}
          <div className="mt-16 text-center">
            <div className="bg-muted rounded-lg p-6 max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Why Choose Solvarsity?
              </h3>
              <div className="grid md:grid-cols-3 gap-6 text-sm">
                <div className="flex items-start space-x-3">
                  <Icon name="BookOpen" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <div>
                    <div className="font-medium text-foreground mb-1">Industry-Relevant Curriculum</div>
                    <div className="text-text-secondary">Updated course content aligned with current market demands</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Users" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <div>
                    <div className="font-medium text-foreground mb-1">Expert Mentorship</div>
                    <div className="text-text-secondary">Learn from industry professionals with years of experience</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="Target" size={20} color="var(--color-primary)" strokeWidth={2} />
                  <div>
                    <div className="font-medium text-foreground mb-1">Guaranteed Placement</div>
                    <div className="text-text-secondary">100% placement assistance with our partner companies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-surface border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-text-secondary">
            <p>© {new Date().getFullYear()} Solvarsity. All rights reserved.</p>
            <div className="flex justify-center space-x-6 mt-4">
              <a href="#" className="hover:text-foreground transition-smooth">Privacy Policy</a>
              <a href="#" className="hover:text-foreground transition-smooth">Terms of Service</a>
              <a href="#" className="hover:text-foreground transition-smooth">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfileSelectionLanding;